import {
  getDashboardByCommission,
  getHierarchy,
  getIndicators,
  getSystemSettings,
  getTracking,
  getWorkPlanLiveTotalsByCommissionIds,
  getWorkPlans
} from '../supabase-service';
import {
  getOperationalStartPeriodConfig,
  parseSelectedMonthIndex
} from './operational-start-period-config';
import {
  detectInstanceOperationalActivation,
  resolveOperationalStartPeriod
} from './operational-start-period-engine';
import {
  getActiveOperationalSettings,
  DEFAULT_OPERATIONAL_CONTROL_SETTINGS,
  loadOperationalSettings
} from './operational-control-settings';
import {
  calculateProratedCompliance,
  getElapsedCompletedMonths
} from '../shared-logic';
import type {
  OperationalControlSettings
} from './operational-control-settings';
import type {
  OperationalAggregatorInput,
  OperationalAlert,
  OperationalControlReadModel,
  OperationalInstanceRow,
  OperationalRawData,
  OperationalStatus,
  OperationalVPRow
} from './types';

const clampPct = (value: number): number => Math.max(0, Math.min(100, Math.round(Number(value || 0))));

const normalizeText = (value: unknown): string =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();

export const isCaptureEntry = (entry: any): boolean => {
  if (!entry) return false;
  const hasExplicitConfirmation = typeof entry.captureConfirmedAt === 'string' && Number.isFinite(Date.parse(entry.captureConfirmedAt));
  const real = Number(entry.real || 0);
  const hasChecklist = Array.isArray(entry.checklist) && entry.checklist.length > 0;
  const hasElements = Array.isArray(entry.elements) && entry.elements.length > 0;
  const hasActivities = Array.isArray(entry.activities) && entry.activities.length > 0;
  return hasExplicitConfirmation || real > 0 || Boolean(entry.note) || hasChecklist || hasElements || hasActivities;
};

const historyHasGoal = (indicator: any): boolean => {
  if (Number(indicator?.metaAnual || indicator?.metaanual || 0) > 0) return true;
  return (indicator?.history || []).some((entry: any) => Number(entry?.meta || 0) > 0);
};

const hasExpiredObligationInMonth = (indicators: any[], monthIndex: number): boolean =>
  indicators.some((indicator) => Number(indicator?.history?.[monthIndex]?.meta || 0) > 0);

const getCurrentMonthIndex = (systemSettings: any, selectedMonthIndex: number): number => {
  const raw = Number(systemSettings?.current_month);
  if (Number.isFinite(raw)) {
    if (raw >= 1 && raw <= 12) return raw - 1;
    if (raw >= 0 && raw <= 11) return raw;
  }
  return selectedMonthIndex;
};

const loadOperationalRawData = async (
  input: OperationalAggregatorInput,
  selectedMonthIndex: number,
  year: number
): Promise<OperationalRawData> => {
  const [hierarchy, dashboard, workPlans, reports, systemSettings] = await Promise.all([
    getHierarchy(input.user),
    getDashboardByCommission(input.user),
    getWorkPlans(input.user),
    getTracking(input.user),
    getSystemSettings()
  ]);

  const instances = [...(hierarchy.commissions || []), ...(hierarchy.independents || [])];
  const instanceIds = instances.map((instance: any) => String(instance.id)).filter(Boolean);
  const [planTotals, indicators] = await Promise.all([
    getWorkPlanLiveTotalsByCommissionIds(instanceIds, year),
    getIndicators(instanceIds)
  ]);

  return {
    hierarchy,
    dashboard,
    workPlans,
    planTotals,
    indicators,
    reports,
    systemSettings: {
      ...systemSettings,
      current_month: getCurrentMonthIndex(systemSettings, selectedMonthIndex)
    }
  };
};

const deriveOperationalStatus = (
  score: number | null,
  staleMonths: number,
  active: boolean,
  onboardingState: string,
  hasData: boolean,
  hasMetaObligation: boolean,
  isSinConfiguracion: boolean,
  isSinMetaVencida: boolean,
  opSettings: OperationalControlSettings
): OperationalStatus => {
  if (isSinConfiguracion) return 'SIN_CONFIGURACION';
  if (isSinMetaVencida) return 'SIN_META_VENCIDA';
  if (!hasMetaObligation) return 'SIN_OBLIGACION';
  if (!active || onboardingState === 'NOT_STARTED') return 'NOT_STARTED';
  if (onboardingState === 'IN_PROGRESS') return 'ONBOARDING';
  if (!hasData) return 'SIN_DATOS';
  if (staleMonths >= opSettings.staleCriticalMonths || (score !== null && score < opSettings.redThreshold)) return 'CRITICO';
  if (staleMonths >= opSettings.staleWarningMonths || (score !== null && score < opSettings.yellowThreshold)) return 'ATENCION';
  return 'AL_DIA';
};

const buildAlerts = (
  row: Omit<OperationalInstanceRow, 'alerts'>,
  opSettings: OperationalControlSettings
): OperationalAlert[] => {
  const alerts: OperationalAlert[] = [];
  const protectedStatuses = new Set<OperationalStatus>([
    'NOT_STARTED', 'ONBOARDING', 'SIN_CONFIGURACION', 'SIN_OBLIGACION', 'SIN_META_VENCIDA', 'SIN_DATOS'
  ]);
  const isProtectedLifecycle = protectedStatuses.has(row.operationalStatus) || !row.resolvedWindow.isOperationallyActive;

  if (!row.resolvedWindow.isOperationallyActive) {
    alerts.push({
      code: 'NOT_OPERATIONALLY_ACTIVE_YET',
      severity: 'info',
      message: 'La instancia aun no entra en su ventana operativa efectiva.'
    });
  }

  if (row.onboardingState === 'IN_PROGRESS') {
    alerts.push({
      code: 'INSTANCE_ONBOARDING_IN_PROGRESS',
      severity: 'info',
      message: 'Instancia en onboarding operativo; no se penaliza como rezago.'
    });
  }

  if (row.operationalStatus === 'SIN_CONFIGURACION') {
    alerts.push({ code: 'NO_CONFIGURATION', severity: 'info', message: 'Faltan parámetros para evaluar la obligación operativa.' });
  }

  if (row.operationalStatus === 'SIN_META_VENCIDA') {
    alerts.push({ code: 'NO_EXPIRED_OBLIGATION', severity: 'info', message: 'No existe obligación vencida en periodos cerrados.' });
  }

  if (row.operationalStatus === 'SIN_OBLIGACION') {
    alerts.push({ code: 'NO_APPLICABLE_OBLIGATION', severity: 'info', message: 'La instancia no tiene obligación aplicable en el periodo.' });
  }

  if (row.operationalStatus === 'SIN_DATOS') {
    alerts.push({ code: 'NO_EVALUABLE_DATA', severity: 'info', message: 'No hay datos evaluables para el periodo.' });
  }

  if (!isProtectedLifecycle && row.activationConfidence === 'LOW') {
    alerts.push({
      code: 'LOW_ACTIVATION_CONFIDENCE',
      severity: 'medium',
      message: 'Inicio operativo inferido con baja confianza; requiere revision ejecutiva futura.'
    });
  }

  if (!isProtectedLifecycle && row.isCurrentMonthPending) {
    alerts.push({
      code: 'CURRENT_MONTH_CAPTURE_PENDING',
      severity: 'medium',
      message: 'Captura del mes en curso pendiente de completar; no se clasifica como rezago vencido.'
    });
  }

  if (!isProtectedLifecycle && !row.currentMonthCapture && !row.isCurrentMonthPending) {
    alerts.push({
      code: 'NO_CURRENT_MONTH_CAPTURE',
      severity: 'medium',
      message: 'Sin captura en el mes operativo seleccionado.'
    });
  }

  if (!isProtectedLifecycle && row.staleMonthsOperational >= opSettings.staleCriticalMonths) {
    alerts.push({
      code: `STALE_${opSettings.staleCriticalMonths}_MONTHS`,
      severity: 'critical',
      message: `${opSettings.staleCriticalMonths} o mas meses operativos esperados sin captura.`
    });
  } else if (!isProtectedLifecycle && row.staleMonthsOperational >= opSettings.staleWarningMonths) {
    alerts.push({
      code: `STALE_${opSettings.staleWarningMonths}_MONTHS`,
      severity: 'medium',
      message: `${opSettings.staleWarningMonths} meses operativos esperados sin captura.`
    });
  }

  if (!isProtectedLifecycle && row.kpiTotal === 0) {
    alerts.push({
      code: 'NO_KPIS',
      severity: 'high',
      message: 'Instancia sin KPIs configurados.'
    });
  } else if (!isProtectedLifecycle && row.kpiTotal > row.kpiWithGoal) {
    alerts.push({
      code: 'KPIS_WITHOUT_GOAL',
      severity: 'medium',
      message: 'Existen KPIs configurados sin meta.'
    });
  }

  if (!isProtectedLifecycle && row.planGoal <= 0) {
    alerts.push({
      code: 'NO_PLAN',
      severity: 'high',
      message: 'Sin plan operativo con meta viva.'
    });
  } else if (!isProtectedLifecycle && !row.planComplete) {
    alerts.push({
      code: 'PLAN_INCOMPLETE',
      severity: 'medium',
      message: 'Plan operativo aun incompleto.'
    });
  }

  return alerts;
};

export const deriveRows = (
  input: OperationalAggregatorInput,
  raw: OperationalRawData,
  selectedMonthIndex: number,
  year: number,
  opSettings: OperationalControlSettings
): OperationalInstanceRow[] => {
  const config = getOperationalStartPeriodConfig(year);
  const currentMonthIndex = Number(raw.systemSettings.current_month);
  const instances = [...(raw.hierarchy.commissions || []), ...(raw.hierarchy.independents || [])];
  const vpMap = new Map<string, string>();
  const vpPresidentMap = new Map<string, string>();

  (raw.hierarchy.vicepresidencies || []).forEach((vp: any) => {
    vpMap.set(String(vp.id), vp.name || vp.vicepresidency_name || 'VP sin nombre');
    vpPresidentMap.set(String(vp.id), vp.president_name || 'Sin asignar');
  });

  const planTotalMap = new Map(raw.planTotals.map((total: any) => [String(total.commission_id), total]));
  const dashboardMap = new Map(raw.dashboard.map((item: any) => [String(item.commission_id), item]));
  const indicatorsByInstance = new Map<string, any[]>();
  const reportsByInstance = new Map<string, any[]>();
  const workPlanByInstance = new Map<string, any>();

  raw.indicators.forEach((indicator: any) => {
    const key = String(indicator.instance_id || indicator.commission_id || '');
    indicatorsByInstance.set(key, [...(indicatorsByInstance.get(key) || []), indicator]);
  });

  raw.reports
    .filter((report: any) => Number(report.year) === year)
    .forEach((report: any) => {
      const key = String(report.commission_id || '');
      reportsByInstance.set(key, [...(reportsByInstance.get(key) || []), report]);
    });

  raw.workPlans
    .filter((plan: any) => Number(plan.year) === year)
    .forEach((plan: any) => {
      const key = String(plan.commission_id || '');
      workPlanByInstance.set(key, plan);
    });

  return instances.map((instance: any) => {
    const instanceId = String(instance.id);
    const vpId = instance.vicepresidency_id ? String(instance.vicepresidency_id) : null;
    const indicators = indicatorsByInstance.get(instanceId) || [];
    const reports = reportsByInstance.get(instanceId) || [];
    const planTotal = planTotalMap.get(instanceId) || null;
    const workPlan = workPlanByInstance.get(instanceId) || null;
    const dashboard = dashboardMap.get(instanceId) || null;

    // Calcular meses válidos donde hay meta > 0 en KPIs o Plan
    const validMonthSet = new Set<number>();
    indicators.forEach((indicator) => {
      (indicator.history || []).forEach((entry: any, idx: number) => {
        if (Number(entry?.meta || 0) > 0) {
          validMonthSet.add(idx);
        }
      });
    });
    const validMonths = Array.from(validMonthSet).sort((a, b) => a - b);

    const lifecycle = detectInstanceOperationalActivation({
      instance,
      indicators,
      reports,
      workPlan,
      planTotal,
      year,
      selectedMonthIndex,
      cycleConfig: config
    });
    const resolvedWindow = resolveOperationalStartPeriod({
      year,
      instanceId,
      vpId,
      selectedMonthIndex,
      currentMonthIndex,
      lifecycle,
      config,
      validMonths
    });

    const planGoal = Number(planTotal?.total_goal || dashboard?.total_goal || workPlan?.total_goal || 0);
    const planProgress = Number(planTotal?.total_progress || dashboard?.total_progress || workPlan?.total_progress || 0);
    const planPct = clampPct(planTotal?.planPct ?? (planGoal > 0 ? (planProgress / planGoal) * 100 : 0));
    const kpiTotal = indicators.length;
    const kpiWithGoal = indicators.filter(historyHasGoal).length;
    const kpiGoalCoverage = kpiTotal > 0 ? clampPct((kpiWithGoal / kpiTotal) * 100) : 0;
    const currentMonthCapture = indicators.some((indicator) => isCaptureEntry(indicator.history?.[selectedMonthIndex]));
    const currentMonthCaptureObligation = indicators.some((indicator) => Number(indicator.history?.[selectedMonthIndex]?.meta || 0) > 0);
    const lastCaptureMonthIndex = indicators.reduce<number | null>((acc, indicator) => {
      const history = indicator.history || [];
      for (let idx = history.length - 1; idx >= 0; idx -= 1) {
        if (isCaptureEntry(history[idx])) return acc === null ? idx : Math.max(acc, idx);
      }
      return acc;
    }, null);
    const capturedMonthSet = new Set<number>();
    indicators.forEach((indicator) => {
      (indicator.history || []).forEach((entry: any, idx: number) => {
        if (isCaptureEntry(entry)) capturedMonthSet.add(idx);
      });
    });

    const hasExplicitOverride = !!config.instanceOverrides[instanceId] || (vpId && !!config.vpOverrides[vpId]);
    const hasMetaObligation = lifecycle.detectedStartMonth !== null || hasExplicitOverride;

    const firstKpiMonthIndex = indicators.reduce<number | null>((acc, indicator) => {
      const history = Array.isArray(indicator.history) ? indicator.history : [];
      const monthIndex = history.findIndex((entry: any) => Number(entry?.meta || 0) > 0);
      if (monthIndex === -1) return acc;
      return acc === null ? monthIndex : Math.min(acc, monthIndex);
    }, null);

    const closedExpectedMonths = resolvedWindow.expectedMonths.filter((monthIndex) => monthIndex < currentMonthIndex);
    const closedMonthsWithExpiredObligation = closedExpectedMonths.filter((monthIndex) =>
      hasExpiredObligationInMonth(indicators, monthIndex)
    );

    const isSinConfiguracion = kpiWithGoal === 0 || firstKpiMonthIndex === null;
    const isSinMetaVencida = !isSinConfiguracion && closedMonthsWithExpiredObligation.length === 0;

    const protectedLifecycle =
      !resolvedWindow.isOperationallyActive ||
      !hasMetaObligation ||
      isSinConfiguracion ||
      isSinMetaVencida ||
      lifecycle.onboardingState === 'NOT_STARTED' ||
      lifecycle.onboardingState === 'IN_PROGRESS';

    const staleMonthsOperational = protectedLifecycle
      ? 0
      : closedMonthsWithExpiredObligation
          .filter((monthIndex) => monthIndex < currentMonthIndex) // Excluir mes en curso del cálculo de stale operativo
          .filter((monthIndex) => !capturedMonthSet.has(monthIndex)).length;

    // Cálculo de KPI Compliance real (cumplimiento ponderado real de KPIs para el periodo seleccionado)
    // REGLA v18.27.73: Si no hay obligación KPI vencida en meses cerrados (isSinMetaVencida),
    // el KPI no es aplicable aunque el mes en curso tenga meta. Produce null (N/A), en
    // paridad exacta con la vista VP que usa liveKpi.eligible=false.
    const kpiComplianceResult = calculateProratedCompliance(indicators, selectedMonthIndex, true);
    const kpiComplianceRaw = kpiComplianceResult.cumplimiento;
    // Si no hay obligación vencida en meses cerrados, el KPI es N/A independientemente del mes en curso.
    const kpiComplianceReal = (isSinMetaVencida || isSinConfiguracion) ? null : kpiComplianceRaw;

    // Contrato de Seguimiento: la evaluación integral VP usa únicamente el
    // último cierre completado. Se conserva kpiCompliance del Control para su
    // columna operativa y se transporta este valor separado solo al agregado VP.
    const canonicalVpMonthIndex = Math.max(0, getElapsedCompletedMonths(selectedMonthIndex));
    const canonicalVpKpiResult = calculateProratedCompliance(indicators, canonicalVpMonthIndex, false, true);
    const canonicalVpKpiCompliance = (isSinMetaVencida || isSinConfiguracion)
      ? null
      : canonicalVpKpiResult.cumplimiento;

    // Blindaje para el mes en curso (v18.19.5): Si es el mes actual y no hay rezago real,
    // usamos el cumplimiento acumulado de los meses anteriores cerrados para el score y la UI,
    // evitando castigar con 0% una captura que aún está abierta.
    let kpiCompliance = kpiComplianceReal;
    let isCurrentMonthPending = false;
    if (kpiComplianceReal !== null && selectedMonthIndex === currentMonthIndex && staleMonthsOperational === 0) {
      const prevAccumResult = selectedMonthIndex > 0
        ? calculateProratedCompliance(indicators, selectedMonthIndex - 1, false)
        : null;
      const prevCompliance = (prevAccumResult && prevAccumResult.cumplimiento !== null)
        ? prevAccumResult.cumplimiento
        : null; // No regalar 100 si no hay histórico real previo
      if (prevCompliance !== null && kpiComplianceReal < prevCompliance) {
        kpiCompliance = prevCompliance;
        isCurrentMonthPending = true;
      }
    }

    // Ponderación y cálculo del score operativo dinámico
    const effectiveKpiWeight = kpiCompliance !== null ? opSettings.kpiWeight : 0;
    const effectivePlanWeight = kpiCompliance !== null ? opSettings.planWeight : 100;
    const kpiContrib = kpiCompliance !== null ? Math.round((kpiCompliance * effectiveKpiWeight) / 100) : 0;
    const planContrib = Math.round((planPct * effectivePlanWeight) / 100);
    const stalePenaltyApplied = opSettings.stalePenaltyEnabled
      ? (staleMonthsOperational * opSettings.stalePenalty)
      : 0;

    const rawScore = kpiContrib + planContrib - stalePenaltyApplied;
    const operationalScore = protectedLifecycle ? null : Math.max(0, Math.min(100, rawScore));
    const hasData = kpiTotal > 0 || planGoal > 0 || reports.length > 0;

    const operationalStatus = deriveOperationalStatus(
      operationalScore,
      staleMonthsOperational,
      resolvedWindow.isOperationallyActive,
      lifecycle.onboardingState,
      hasData,
      hasMetaObligation,
      isSinConfiguracion,
      isSinMetaVencida,
      opSettings
    );

    // Información adicional; no alimenta el KPI certificado, score o integral.
    // El tablero canónico declara ciclo 2026 y utiliza el cierre calendario.
    const closedIndex = year === 2026 ? getElapsedCompletedMonths(selectedMonthIndex) : -1;
    const closedResult = closedIndex >= 0 && indicators.length > 0
      ? calculateProratedCompliance(indicators, closedIndex, false, true)
      : null;
    const monthlyReason = indicators.length === 0 ? 'Sin indicadores configurados'
      : kpiWithGoal === 0 ? 'Sin configuración de metas'
      : kpiComplianceResult.activeCount === 0 ? 'Sin meta en el mes'
      : kpiComplianceReal === null ? 'Sin captura exigible (sin meta vencida)'
      : !currentMonthCapture ? 'Captura ausente · indicadores evaluables'
      : 'Indicadores evaluables';
    const indicatorDisplay = {
      monthlyValue: kpiComplianceReal,
      monthlyPeriod: `${['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][selectedMonthIndex]} ${year}`,
      monthlyReason,
      closedValue: closedResult?.activeWeight ? closedResult.cumplimiento : null,
      closedActiveCount: closedResult?.activeCount ?? 0,
      closedPeriod: closedIndex >= 0
        ? `Ene–${['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][closedIndex]} ${year}`
        : 'Sin periodo cerrado canónico'
    };

    const baseRow: any = {
      instanceId,
      instanceName: instance.name || instance.commission_name || 'Instancia sin nombre',
      instanceType: normalizeText(instance.type || 'INSTANCIA'),
      instancePresident: instance.president_name || 'Sin asignar',
      vpId,
      vpName: vpId ? vpMap.get(vpId) || 'VP sin nombre' : 'Sin VP',
      vpPresident: vpId ? vpPresidentMap.get(vpId) || 'Sin asignar' : 'Sin asignar',
      operationalStatus,
      operationalScore,
      planPct,
      planComplete: planPct >= 100,
      planGoal,
      planProgress,
      kpiTotal,
      kpiWithGoal,
      kpiGoalCoverage,
      indicatorDisplay,
      kpiCompliance,
      canonicalVpKpiCompliance,
      isCurrentMonthPending,
      kpiWeight: opSettings.kpiWeight,
      planWeight: opSettings.planWeight,
      stalePenaltyApplied,
      currentMonthCapture,
      currentMonthCaptureObligation,
      operationalStartMonth: resolvedWindow.startMonth,
      operationalStartSource: resolvedWindow.source,
      lifecycleSource: lifecycle.detectedStartSource,
      activationConfidence: lifecycle.detectionConfidence,
      onboardingState: lifecycle.onboardingState,
      staleMonthsOperational,
      lastCaptureMonthIndex,
      firstKpiMonthIndex,
      resolvedWindow
    };

    return {
      ...baseRow,
      alerts: buildAlerts(baseRow, opSettings)
    };
  });
};

const summarizeRows = (rows: OperationalInstanceRow[]) => {
  const activeRows = rows.filter((row) => row.operationalStatus !== 'SIN_OBLIGACION' && row.operationalStatus !== 'SIN_CONFIGURACION' && row.operationalStatus !== 'SIN_META_VENCIDA');
  const activeScores = activeRows.map((row) => row.operationalScore).filter((score): score is number => score !== null);
  const totalInstances = activeRows.length;
  const currentCaptureCount = activeRows.filter((row) => row.currentMonthCapture).length;
  const totalKpis = activeRows.reduce((acc, row) => acc + row.kpiTotal, 0);
  const totalKpisWithGoal = activeRows.reduce((acc, row) => acc + row.kpiWithGoal, 0);

  return {
    healthScore: activeScores.length ? clampPct(activeScores.reduce((acc, score) => acc + score, 0) / activeScores.length) : 0,
    totalInstances,
    criticalInstances: activeRows.filter((row) => row.operationalStatus === 'CRITICO').length,
    currentCaptureCount,
    currentCapturePct: totalInstances ? clampPct((currentCaptureCount / totalInstances) * 100) : 0,
    completePlans: activeRows.filter((row) => row.planComplete).length,
    kpisWithGoalPct: totalKpis ? clampPct((totalKpisWithGoal / totalKpis) * 100) : 0,
    onboardingInstances: activeRows.filter((row) => row.operationalStatus === 'ONBOARDING').length,
    staleInstances: activeRows.filter((row) => row.staleMonthsOperational >= 2).length
  };
};

const buildVpRows = (rows: OperationalInstanceRow[]): OperationalVPRow[] => {
  const groups = new Map<string, OperationalInstanceRow[]>();
  rows.forEach((row) => {
    const key = row.vpId || 'NO_VP';
    groups.set(key, [...(groups.get(key) || []), row]);
  });

  return Array.from(groups.entries())
    .map(([vpId, group]) => {
      const activeGroup = group.filter((row) => row.operationalStatus !== 'SIN_OBLIGACION' && row.operationalStatus !== 'SIN_CONFIGURACION' && row.operationalStatus !== 'SIN_META_VENCIDA');
      const scores = activeGroup.map((row) => row.operationalScore).filter((score): score is number => score !== null);
      return {
        vpId: vpId === 'NO_VP' ? null : vpId,
        vpName: group[0]?.vpName || 'Sin VP',
        totalInstances: activeGroup.length,
        averageScore: scores.length ? clampPct(scores.reduce((acc, score) => acc + score, 0) / scores.length) : 0,
        criticalInstances: activeGroup.filter((row) => row.operationalStatus === 'CRITICO').length,
        onboardingInstances: activeGroup.filter((row) => row.operationalStatus === 'ONBOARDING').length,
        noCurrentCapture: activeGroup.filter((row) => !row.currentMonthCapture && row.resolvedWindow.isOperationallyActive).length,
        staleInstances: activeGroup.filter((row) => row.staleMonthsOperational >= 2).length
      };
    })
    .filter((vp) => vp.totalInstances > 0)
    .sort((a, b) => b.criticalInstances - a.criticalInstances || b.staleInstances - a.staleInstances);
};

const mapSystemSettingsToOperational = (sys: any): OperationalControlSettings => {
  const green = Number(sys?.threshold_green ?? sys?.greenThreshold ?? 90);
  const yellow = Number(sys?.threshold_amber ?? sys?.yellowThreshold ?? 75);
  const orange = Number(sys?.threshold_orange ?? sys?.orangeThreshold ?? 60);
  const red = Number(sys?.threshold_red ?? sys?.redThreshold ?? 60);
  const kpiWeight = Number(sys?.kpi_weight ?? sys?.weight_dimension_kpis ?? sys?.kpiWeight ?? 70);
  const planWeight = Number(sys?.plan_weight ?? sys?.weight_dimension_plans ?? sys?.planWeight ?? 30);
  const stalePenalty = Number(sys?.stale_penalty ?? sys?.stalePenalty ?? 10);
  const stalePenaltyEnabled = sys?.stale_penalty_enabled !== undefined ? Boolean(sys?.stale_penalty_enabled) : Boolean(sys?.stalePenaltyEnabled ?? true);

  return {
    greenThreshold: green,
    yellowThreshold: yellow,
    orangeThreshold: orange,
    redThreshold: red,
    kpiWeight,
    planWeight,
    weightComisiones: Number(sys?.weight_comisiones ?? 50),
    weightComites: Number(sys?.weight_comites ?? 30),
    weightConsejerias: Number(sys?.weight_consejerias ?? 20),
    stale1Months: 1,
    stale2Months: 2,
    stale3Months: 3,
    stalePenalty,
    stalePenaltyEnabled,
    quarterlyHardeningEnabled: Boolean(sys?.quarterly_hardening ?? false),
    staleWarningMonths: 2,
    staleCriticalMonths: 3
  };
};

export const buildOperationalControlReadModel = async (
  input: OperationalAggregatorInput
): Promise<OperationalControlReadModel> => {
  const year = input.year || 2026;
  const selectedMonthIndex = parseSelectedMonthIndex(input.selectedMonth);
  const raw = await loadOperationalRawData(input, selectedMonthIndex, year);
  // Unificar lectura: priorizar DB settings (raw.systemSettings) y caer a localStorage/defaults
  const baseSettings = raw.systemSettings ? mapSystemSettingsToOperational(raw.systemSettings) : loadOperationalSettings();
  const opSettings = getActiveOperationalSettings(selectedMonthIndex, baseSettings);
  const rows = deriveRows(input, raw, selectedMonthIndex, year, opSettings);
  const alerts = rows
    .filter((row) => row.operationalStatus !== 'SIN_OBLIGACION')
    .flatMap((row) =>
      row.alerts.map((alert) => ({
        ...alert,
        instanceId: row.instanceId,
        instanceName: row.instanceName,
        vpName: row.vpName
      }))
    );

  return {
    user: input.user,
    year,
    selectedMonth: input.selectedMonth,
    selectedMonthIndex,
    generatedAt: new Date().toISOString(),
    summary: summarizeRows(rows),
    rows,
    scopeVicepresidencies: (raw.hierarchy.vicepresidencies || []).map((vp: any) => ({
      id: String(vp.id),
      name: vp.name || vp.vicepresidency_name || 'VP sin nombre',
      presidentName: vp.president_name || 'Sin asignar'
    })),
    vpRows: buildVpRows(rows),
    alerts,
    opSettings
  };
};
